import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '../../api/client'
import type { AiProvidersResponse } from '../../api/schema'
import { notify } from '../../lib/notifications'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'

const CODES = {
    internal: 'ai.agent.internal',
    providers: 'ai.agent.allowed_providers',
    writeMode: 'ai.agent.write_mode',
    maxRuns: 'ai.agent.max_concurrent_runs',
    routerModel: 'ai.assistant.router_model',
} as const

/** The Select value of an empty router model: Radix refuses an empty value. */
const ROUTER_OFF = '__off__'

/**
 * routerModelOptions are the models of the OpenAI-compatible providers the
 * organization allows (spec 2026-10-21-24 §2), and the current value.
 */
export function routerModelOptions(
    providers: { name: string; type: string; models?: string[] | null }[],
    allowed: string[],
    current: string,
): string[] {
    const out: string[] = []
    for (const p of providers) {
        if (p.type !== 'openai_compatible' || !allowed.includes(p.name)) continue
        for (const m of p.models ?? []) if (!out.includes(m)) out.push(m)
    }
    if (current && !out.includes(current)) out.push(current)
    return out
}

/** The setting errors with their own message (spec 2026-10-21-19 §6). */
const KNOWN_ERRORS = ['AGENT_DIRECT_NEEDS_INTERNAL', 'ai_choice_invalid', 'setting_value_invalid', 'setting_write_restricted']

type Writer = {
    POST: (
        path: string,
        opts: { params: { path: { orgCode: string; code: string } }; body: { value: unknown } }
    ) => Promise<{ error?: { code?: string } }>
}

type Agent = AiProvidersResponse['agent']
type Change = { code: string; value: unknown }

/**
 * The agent settings of the organization (spec 2026-10-21-19 §6): internal,
 * the allowed providers, the write mode and the concurrent runs. Platform
 * admins write them, everyone else reads them.
 */
export function AgentSettingsSection({
    org,
    agent,
    providers,
    canWrite,
    queryKey,
}: {
    org: string
    agent: Agent
    providers: AiProvidersResponse['providers']
    canWrite: boolean
    queryKey: unknown[]
}) {
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()
    const [revoke, setRevoke] = useState<Change | null>(null)
    const [maxRuns, setMaxRuns] = useState(String(agent.max_concurrent_runs))
    useEffect(() => setMaxRuns(String(agent.max_concurrent_runs)), [agent.max_concurrent_runs])

    const agentProviders = providers.filter((p) => p.type === 'anthropic' || p.type === 'openai_compatible')

    const mutation = useMutation({
        mutationFn: async (change: Change) => {
            const res = await (apiClient as unknown as Writer).POST('/api/v1/organizations/{orgCode}/settings/{code}', {
                params: { path: { orgCode: org, code: change.code } },
                body: { value: change.value },
            })
            if (res.error) throw res.error
        },
        onSuccess: () => notify({ key: 'ai_models_saved', variant: 'default', titleKey: 'settings:ai_models.saved' }),
        onError: (err: { code?: string }) => {
            const code = err?.code && KNOWN_ERRORS.includes(err.code) ? err.code : 'generic'
            notify({
                key: 'ai_models_agent_error',
                variant: 'destructive',
                titleKey: 'settings:ai_models.error_title',
                descriptionKey: `settings:ai_models.agents.errors.${code}`,
                timeoutMs: 0,
            })
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey })
            await queryClient.invalidateQueries({
                predicate: (q) => {
                    const key = q.queryKey as unknown[]
                    return key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/agent-missions'
                },
            })
        },
    })

    const write = (change: Change, revokes: boolean) => (revokes ? setRevoke(change) : mutation.mutate(change))
    const saveMaxRuns = () => {
        const n = Number(maxRuns)
        if (!Number.isInteger(n) || n < 1 || n > 10) {
            setMaxRuns(String(agent.max_concurrent_runs))
            return
        }
        if (n !== agent.max_concurrent_runs) mutation.mutate({ code: CODES.maxRuns, value: n })
    }
    const billing = (b?: string) => t(`ai_models.agents.billing_${b === 'subscription' ? 'subscription' : 'per_token'}`)

    return (
        <section className="bg-card rounded-lg border border-border p-6 space-y-5" data-testid="ai-models-agents">
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('ai_models.agents.title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('ai_models.agents.description')}</p>
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div>
                    <div className="text-sm font-medium text-foreground">{t('ai_models.agents.internal')}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t('ai_models.agents.internal_hint')}</p>
                </div>
                {canWrite ? (
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={agent.internal}
                            disabled={mutation.isPending}
                            onCheckedChange={(checked) => write({ code: CODES.internal, value: checked }, !checked)}
                            data-testid="agent-settings-internal"
                        />
                        {t('ai_models.agents.internal_label')}
                    </label>
                ) : (
                    <div className="text-sm text-foreground" data-testid="agent-settings-internal-value">
                        {agent.internal ? t('ai_models.agents.yes') : t('ai_models.agents.no')}
                    </div>
                )}
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div className="text-sm font-medium text-foreground">{t('ai_models.agents.providers')}</div>
                <div className="space-y-2">
                    {agentProviders.length === 0 && (
                        <div className="text-sm text-muted-foreground">{t('ai_models.agents.no_providers')}</div>
                    )}
                    {agentProviders.map((p) => {
                        const allowed = agent.allowed_providers.includes(p.name)
                        return canWrite ? (
                            <label key={p.name} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={allowed}
                                    disabled={mutation.isPending}
                                    onCheckedChange={(checked) => {
                                        const next = checked
                                            ? [...agent.allowed_providers, p.name]
                                            : agent.allowed_providers.filter((n) => n !== p.name)
                                        write({ code: CODES.providers, value: next }, !checked)
                                    }}
                                    data-testid={`agent-settings-provider-${p.name}`}
                                />
                                <span className="text-foreground">{p.name}</span>
                                <span className="text-xs text-muted-foreground">{billing(p.billing)}</span>
                            </label>
                        ) : (
                            <div key={p.name} className="text-sm" data-testid={`agent-settings-provider-${p.name}-value`}>
                                <span className={allowed ? 'text-foreground' : 'text-muted-foreground line-through'}>{p.name}</span>
                                <span className="ml-2 text-xs text-muted-foreground">{billing(p.billing)}</span>
                            </div>
                        )
                    })}
                    {canWrite && (
                        <p className="text-xs text-muted-foreground" data-testid="agent-settings-subprocessor-note">
                            {t('ai_models.agents.subprocessor_note')}
                        </p>
                    )}
                </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div>
                    <div className="text-sm font-medium text-foreground">{t('ai_models.agents.write_mode')}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t('ai_models.agents.write_mode_hint')}</p>
                </div>
                {canWrite ? (
                    <Select
                        value={agent.write_mode}
                        onValueChange={(v) => mutation.mutate({ code: CODES.writeMode, value: v })}
                        disabled={mutation.isPending}
                    >
                        <SelectTrigger data-testid="agent-settings-write-mode">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="propose" data-testid="agent-settings-write-mode-option-propose">
                                {t('ai_models.agents.write_mode_propose')}
                            </SelectItem>
                            <SelectItem value="direct" disabled={!agent.internal} data-testid="agent-settings-write-mode-option-direct">
                                {t('ai_models.agents.write_mode_direct')}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-foreground" data-testid="agent-settings-write-mode-value">
                        {t(`ai_models.agents.write_mode_${agent.write_mode}`)}
                    </div>
                )}
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div>
                    <div className="text-sm font-medium text-foreground">{t('ai_models.agents.router_model')}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t('ai_models.agents.router_model_hint')}</p>
                </div>
                {canWrite ? (
                    <Select
                        value={agent.assistant_router_model || ROUTER_OFF}
                        onValueChange={(v) => mutation.mutate({ code: CODES.routerModel, value: v === ROUTER_OFF ? '' : v })}
                        disabled={mutation.isPending}
                    >
                        <SelectTrigger data-testid="agent-settings-router-model">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ROUTER_OFF} data-testid="agent-settings-router-model-option-off">
                                {t('ai_models.agents.router_model_off')}
                            </SelectItem>
                            {routerModelOptions(providers, agent.allowed_providers, agent.assistant_router_model ?? '').map((m) => (
                                <SelectItem key={m} value={m} data-testid={`agent-settings-router-model-option-${m}`}>
                                    {m}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-foreground" data-testid="agent-settings-router-model-value">
                        {agent.assistant_router_model || t('ai_models.agents.router_model_off')}
                    </div>
                )}
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div>
                    <div className="text-sm font-medium text-foreground">{t('ai_models.agents.max_runs')}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t('ai_models.agents.max_runs_hint')}</p>
                </div>
                {canWrite ? (
                    <Input
                        type="number"
                        min={1}
                        max={10}
                        step={1}
                        value={maxRuns}
                        disabled={mutation.isPending}
                        onChange={(e) => setMaxRuns(e.target.value)}
                        onBlur={saveMaxRuns}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveMaxRuns()
                        }}
                        data-testid="agent-settings-max-runs"
                    />
                ) : (
                    <div className="text-sm text-foreground" data-testid="agent-settings-max-runs-value">
                        {agent.max_concurrent_runs}
                    </div>
                )}
            </div>

            {!canWrite && <p className="text-xs text-muted-foreground">{t('ai_models.read_only')}</p>}

            <AlertDialog open={revoke !== null} onOpenChange={(open) => !open && setRevoke(null)}>
                <AlertDialogContent data-testid="agent-settings-revoke-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('ai_models.agents.revoke_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('ai_models.agents.revoke_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="agent-settings-revoke-cancel">{t('ai_models.agents.revoke_cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="agent-settings-revoke-submit"
                            onClick={() => {
                                if (revoke) mutation.mutate(revoke)
                                setRevoke(null)
                            }}
                        >
                            {t('ai_models.agents.revoke_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    )
}
